import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const DISMISS_KEY = 'pwa-banner-dismissed';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isDismissed(): boolean {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return Date.now() - parseInt(ts, 10) < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches;
}

function isMobileDevice(): boolean {
  return window.innerWidth < 960;
}

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [ios, setIos] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  useEffect(() => {
    if (!isMobileDevice() || isStandalone() || isDismissed()) return;

    if (isIOS()) {
      setIos(true);
      setVisible(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      dismiss();
    }
  };

  const dismiss = () => {
    setAnimateOut(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setTimeout(() => setVisible(false), 320);
  };

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        display: { xs: 'flex', md: 'none' },
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        background: 'rgba(13, 27, 53, 0.92)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        animation: animateOut
          ? 'slideOutUp 320ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
          : 'slideInDown 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        '@keyframes slideInDown': {
          from: { transform: 'translateY(-100%)' },
          to: { transform: 'translateY(0)' },
        },
        '@keyframes slideOutUp': {
          from: { transform: 'translateY(0)' },
          to: { transform: 'translateY(-100%)' },
        },
      }}
    >
      {/* App icon */}
      <Box
        component="img"
        src="/logo-icon.svg"
        alt="Lysi"
        sx={{ width: 28, height: 28, flexShrink: 0 }}
      />

      {/* Text */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          sx={{ color: 'white', fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}
        >
          Instalar Lysi-Web
        </Typography>
        {ios ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, lineHeight: 1.3 }}>
            Toca ⬆ Compartir → &quot;Agregar a pantalla de inicio&quot;
          </Typography>
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>
            Agrega a la pantalla de inicio
          </Typography>
        )}
      </Box>

      {/* Install button — only for non-iOS */}
      {!ios && (
        <Button
          size="small"
          variant="contained"
          onClick={handleInstall}
          sx={{
            bgcolor: 'white',
            color: '#0D1B35',
            fontWeight: 700,
            fontSize: 12,
            px: 1.5,
            py: 0.5,
            minHeight: 0,
            minWidth: 0,
            borderRadius: '6px',
            flexShrink: 0,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
          }}
        >
          Instalar
        </Button>
      )}

      {/* Dismiss */}
      <IconButton
        size="small"
        onClick={dismiss}
        sx={{ color: 'rgba(255,255,255,0.6)', flexShrink: 0, p: 0.5 }}
        aria-label="Cerrar"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
